import { Grid } from '@/common/grid/Grid';
import { useEffect, useRef, useState } from 'react';

// URA is a per-unit rate, so it needs more than cents to reproduce the amounts
const RATE_PRECISION = 6;
const ura = (prefix) => ({
  colId: prefix + 'Ura',
  field: 'ura',
  displayName: 'URA',
  type: 'MONEY',
  decimalPrecision: RATE_PRECISION,
});

// Each metric shows Units × rate = Amount; Gross Sale prices the Paid units at WAC instead of URA
const fieldDefs = [
  { field: 'invLineCode', displayName: 'Invoice Line', type: 'HYPERLINK', module: 'sc', entity: 'MCD_INV_LINE' },
  { field: 'invPeriodCode', displayName: 'Rebate Period' },
  { field: 'earnedPeriodCode', displayName: 'Earned Period' },
  { field: 'stateName', displayName: 'State' },
  { field: 'ctrtName', displayName: 'Contract' },
  { field: 'brandCode', displayName: 'Brand' },
  { field: 'ndc11Code', displayName: 'NDC11' },
  {
    displayName: 'Gross Sale',
    children: [
      { colId: 'grossSaleUnits', field: 'paidUnits', displayName: 'Units', type: 'NUMBER' },
      { field: 'wacPerUnit', displayName: 'WAC Unit Price', type: 'MONEY', decimalPrecision: 2 },
      { field: 'grossSaleAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Invoiced',
    children: [
      { field: 'invUnits', displayName: 'Units', type: 'NUMBER' },
      ura('inv'),
      { field: 'invAmt', displayName: 'Amount', type: 'MONEY' },
      { field: 'interestAmt', displayName: 'Interest', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Paid',
    children: [
      { field: 'paidUnits', displayName: 'Units', type: 'NUMBER' },
      ura('paid'),
      { field: 'paidAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Due',
    children: [
      { field: 'dueUnits', displayName: 'Units', type: 'NUMBER' },
      ura('due'),
      { field: 'dueAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'Disputed',
    children: [
      { field: 'dispUnits', displayName: 'Units', type: 'NUMBER' },
      ura('disp'),
      { field: 'dispAmt', displayName: 'Amount', type: 'MONEY' },
    ],
  },
  {
    displayName: 'WAC',
    children: [
      // Differs from the line's NDC11 when that one has no WAC and a fallback NDC11's is used
      { field: 'wacNdc11Code', displayName: 'NDC11' },
      { field: 'wacPerPackage', displayName: 'Package Price', type: 'MONEY' },
      { field: 'wacUpps', displayName: 'UPPS', type: 'NUMBER' },
      { field: 'wacStart', displayName: 'Start', type: 'DATE' },
      { field: 'wacEnd', displayName: 'End', type: 'DATE' },
    ],
  },
];

// Maps each x-axis dimension to the grid column that filtering by it corresponds to
const xAxisToField = {
  RBT_PERIOD: 'invPeriodCode',
  EARNED_PERIOD: 'earnedPeriodCode',
  NDC11_PROD: 'ndc11Code',
  BRAND: 'brandCode',
  STATE: 'stateName',
  CTRT: 'ctrtName',
};

// Row-per-invoice-line companion to the trend chart, scoped to the same filters as the chart.
// The clicked bar only pre-sets a removable grid filter; it never narrows the underlying query.
export const McdInvTrendDetailGrid = ({ rows, xAxis, category }) => {
  const gridRef = useRef();
  const [isGridReady, setIsGridReady] = useState(false);

  // ag-grid's onGridReady fires asynchronously; setDataSource before then throws
  useEffect(() => {
    if (!isGridReady) return;
    gridRef.current?.setDataSource(rows);

    const field = xAxisToField[xAxis];
    const gridApi = gridRef.current?.gridApiRef.current;
    if (field && category) {
      gridApi?.setFilterModel({ [field]: { filterType: 'text', type: 'equals', filter: category } });
    } else {
      gridApi?.setFilterModel(null);
    }
  }, [isGridReady, rows, xAxis, category]);

  return (
    <Grid
      ref={gridRef}
      gridOptions={{ fieldDefs, rowModelType: 'clientSide' }}
      events={{ onGridReady: () => setIsGridReady(true) }}
    />
  );
};
